import { useNavigate } from 'react-router';
import { ArrowRight, CalendarClock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  useDueFundReports,
  useGenerateFundReport,
} from '@/hooks/react-query/useFundReports';
import type { DueReport } from '@/types/fundReport';

/**
 * A nudge, not a worklist: the periods a fund still owes, newest first.
 *
 * Only the head of the backlog is shown. The rest is the New report page's
 * job, which picks any period from the same list, so nothing here has to grow
 * into a second table.
 */
const SHOWN = 3;

export function ReportSuggestions({ fundId }: { fundId: string }) {
  const navigate = useNavigate();
  const generateReport = useGenerateFundReport(fundId);
  // A hint, so a spinner or an error card would cost more than it tells.
  const { data: due = [] } = useDueFundReports(fundId);

  if (due.length === 0) return null;

  const start = (report: DueReport) =>
    generateReport.mutate(
      {
        report_definition_id: report.report_definition_id,
        period_end: report.period_end,
      },
      {
        onSuccess: (created) =>
          navigate(`/app/funds/${fundId}/reports/${created.id}`),
      }
    );

  return (
    <div className="space-y-3 rounded-md border border-primary/40 bg-primary/5 p-3">
      <div className="flex items-center gap-2 text-sm font-medium text-foreground">
        <CalendarClock className="h-4 w-4 text-primary" />
        {due.length === 1 ? '1 report is due' : `${due.length} reports are due`}
      </div>

      {due.slice(0, SHOWN).map((report, index) => (
        <div
          key={`${report.report_definition_id}-${report.period_end}`}
          className="flex flex-wrap items-center justify-between gap-3 pl-6"
        >
          <span className="text-sm text-foreground">
            {report.report_name} · {report.period_label}
          </span>
          <Button
            size="sm"
            variant={index === 0 ? 'default' : 'outline'}
            onClick={() => start(report)}
            disabled={generateReport.isPending}
          >
            Start {report.period_label}
          </Button>
        </div>
      ))}

      {due.length > SHOWN && (
        <Button
          variant="link"
          size="sm"
          className="h-auto p-0 pl-6 text-sm"
          onClick={() => navigate(`/app/funds/${fundId}/reports/new`)}
        >
          See all {due.length} due periods
          <ArrowRight className="ml-1 h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
}
